import React, { useEffect, useState, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Button, PageHeader, Form } from 'antd'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { errorMessage, successMessage } from 'services/messager'
import {
	getOptions,
	getSolicitantAreasOptions,
	createWorkflow,
	createDemand,
	getTechnicalAreasOptions,
} from 'services/demands/demandsServices'
import { getCompanyTeams } from 'services/demands/teamsServices'
import FormSection from './components/FormSection'
import { mimeTypes } from 'utils/file'
import { priorities } from 'utils/constants'
import FieldForm from './components/fieldForm'
import { VendorReviewFormQuestions } from './constants/vendorReviewFormQuestions'

import { StyledForm, StyledFormItem } from './styles'
import styles from './index.module.scss'
import FormFactory from 'components/formFactory'
import useTemplateVarsForm from './hooks/useTemplateVarsForm'
import { appendAnswer } from 'states/modules/answer'
import { nextPage, previousPage } from 'states/modules/question'
import { getAnalysisObjectsOptions } from 'services/demands/demandsServices'

const ALLCARE_COMPANY_ID =
	Number(process.env?.REACT_APP_ALLCARE_COMPANY_ID) || 1

const vendorReviewFieldTypeMap = {
	short_text: 'input',
	paragraph: 'textarea',
	single_select: 'select',
	multi_select: 'multiselect',
}

const vendorReviewFieldName = (questionKey) => `vendorReview_${questionKey}`

const vendorReviewDefaultValues = VendorReviewFormQuestions.reduce(
	(acc, question) => {
		acc[vendorReviewFieldName(question.key)] =
			question.type === 'multi_select' ? [] : undefined
		return acc
	},
	{}
)

const CreateDemand = () => {
	const history = useHistory()
	const queryClient = useQueryClient()
	const dispatch = useDispatch()
	const [templateVariablesForm] = Form.useForm()

	const user = useSelector(({ session }) => session)
	const answer = useSelector(({ answer }) => answer)

	const { control, handleSubmit, watch, setValue, reset, trigger } = useForm({
		defaultValues: {
			taskName: '',
			description: '',
			categoryId: undefined,
			templateId: undefined,
			priority: undefined,
			fileList: [],
			solicitantAreaId: undefined,
			management_team_id: undefined,
			legal_team_id: undefined,
			technical_area_team_id: undefined,
			diretoria_team_id: undefined,
			optionalRoute: undefined,
			...vendorReviewDefaultValues,
		},
	})

	const [currentStep, setCurrentStep] = useState(0)
	const [moduleTooltip, setModuleTooltip] = useState('')
	const [requireTemplateFields, setRequireTemplateFields] = useState(false)
	const [isUploadTemplate, setIsUploadTemplate] = useState(false)
	const [requiresVendorReviewForm, setRequiresVendorReviewForm] =
		useState(false)

	const watchValues = watch()

	const taskNameLabel =
		user.company_id === ALLCARE_COMPANY_ID
			? 'Título do chamado'
			: 'Fornecedor/Contraparte'
	const isAllCareUser = user.company_id === ALLCARE_COMPANY_ID

	const { loading: isLoadingTemplateVarsForm } = useTemplateVarsForm(
		watchValues.templateId
	)

	const { data: categories, isLoading: isLoadingCategories } = useQuery({
		queryKey: ['category-options'],
		queryFn: async () => {
			try {
				const data = await getOptions({
					route: '/categories/list',
					page: 1,
					limit: 100,
				})

				return data.list
			} catch {
				errorMessage({ content: 'Erro ao obter categorias' })
			}
		},
	})

	const { data: models, isLoading: isLoadingModels } = useQuery({
		queryKey: ['models-options'],
		queryFn: async () => {
			try {
				const data = await getOptions({
					route: '/templates/list',
					page: 1,
					limit: 100,
				})
				return data.list
			} catch {
				errorMessage({ content: 'Erro ao obter modelos' })
			}
		},
	})

	const { data: solicitantAreas, isLoading: isLoadingAreas } = useQuery({
		queryKey: ['solicitant-areas-options'],
		queryFn: () => getSolicitantAreasOptions(''),
	})

	const { data: technicalAreas, isLoading: isLoadingTechnicalAreas } = useQuery(
		{
			queryKey: ['technical-areas-options'],
			queryFn: () => getTechnicalAreasOptions(''),
		}
	)

	const { data: analysisObjects, isLoading: isLoadingAnalysisObjects } =
		useQuery({
			queryKey: ['analysis-objects-options'],
			queryFn: async () => {
				try {
					const data = await getAnalysisObjectsOptions(1, 100)
					return data || []
				} catch {
					errorMessage({ content: 'Erro ao obter objetos de análise' })
				}
			},
		})

	const { mutate: newDemand, isPending: isLoadingDemand } = useMutation({
		mutationFn: async (values) => {
			const workflowFormData = new FormData()
			const workflowPayload = {
				taskName: values.taskName,
				priority: values.priority,
				categoryId: values.categoryId,
				description: values.description,
				solicitantAreaId: values.solicitantAreaId,
				managementAreaId: values.managementAreaId,
				analysisObjectId: values?.analysisObjectId || null,
				resume: values?.resume,
				templateId: values.templateId,
				variables: values.variables,
				technicalAreaTeamId: values.technicalAreaTeamId,
			}

			if (requiresVendorReviewForm) {
				const vendorReviewAnswers = VendorReviewFormQuestions.reduce(
					(acc, question) => {
						const fieldValue = values[vendorReviewFieldName(question.key)]
						const isEmpty =
							fieldValue === undefined ||
							fieldValue === null ||
							fieldValue === '' ||
							(Array.isArray(fieldValue) && fieldValue.length === 0)
						if (!isEmpty) acc[question.key] = fieldValue
						return acc
					},
					{}
				)
				workflowPayload.vendorReviewForm = JSON.stringify(vendorReviewAnswers)
			}

			Object.entries(workflowPayload).forEach(([key, value]) => {
				if (value !== undefined && value !== null) {
					workflowFormData.append(key, value)
				}
			})

			if (values.fileList?.length > 0) {
				values.fileList.forEach((file) => {
					const actualFile = file.originFileObj ? file.originFileObj : file
					workflowFormData.append('files', actualFile)
				})
			}

			if (watchValues.categoryId === selectedWorkflowCategory?.id) {
				return await createWorkflow(workflowFormData)
			} else {
				return await createDemand(workflowFormData)
			}
		},
		onSuccess: () => {
			successMessage({ content: 'Nova demanda cadastrada com sucesso!' })
			reset()
			queryClient.invalidateQueries({ queryKey: ['demands'] })
			history.push('/demands')
		},
		onError: (error) => {
			console.error(error)
			errorMessage({
				content: error.message || 'Erro ao cadastrar nova demanda.',
			})
		},
	})

	const onSubmit = (values) => newDemand(values)

	const handleGoBack = () => {
		reset()
		history.push('/demands')
	}

	useEffect(() => {
		if (!models || !watchValues.templateId) return
		const template = models.find((m) => m.id === watchValues.templateId)
		setModuleTooltip(template?.config?.description ?? '')
	}, [models, watchValues.templateId])

	const handleCategoryChange = (value, fieldOnChange) => {
		const found = categories?.find((c) => c.id === value)
		setRequireTemplateFields(Boolean(found?.require_template))
		setRequiresVendorReviewForm(Boolean(found?.requires_vendor_review_form))
		setValue('templateId', undefined)
		fieldOnChange(value)
	}

	const handleTemplateChange = (value, fieldOnChange) => {
		const found = models?.find((t) => t.id === value)
		setIsUploadTemplate(found?.text_type === 'upload')
		fieldOnChange(value)
	}

	const enrichedWatchValues = {
		...watchValues,
		_requireTemplate: requireTemplateFields,
		_hasModuleTooltip: Boolean(moduleTooltip),
		_moduleTooltip: moduleTooltip,
	}

	const selectedWorkflowCategory = useMemo(
		() =>
			categories?.find(
				(c) => c.id === watchValues.categoryId && c.has_workflow
			),
		[watchValues.categoryId, categories]
	)

	const step0Fields = [
		{
			type: 'select',
			name: 'solicitantAreaId',
			label: 'Área Solicitante',
			required: true,
			placeholder: 'Selecione a área solicitante',
			options: solicitantAreas?.filter((area) => !area.onlyManagement),
			isLoading: isLoadingAreas,
			allowClear: true,
			rules: { required: 'Selecione uma área solicitante' },
		},
		{
			type: 'input',
			name: 'taskName',
			label: taskNameLabel,
			required: true,
		},
		{
			type: 'textarea',
			name: 'description',
			label: isAllCareUser
				? 'Descrição sobre as condições técnicas, comerciais e negociais'
				: 'Descrição',
			required: true,
		},
		{
			type: 'select',
			name: 'categoryId',
			label: 'Categoria',
			required: true,
			options: categories,
			isLoading: isLoadingCategories,
			rules: { required: 'Selecione uma categoria' },
			onChange: handleCategoryChange,
		},
		{
			type: 'select',
			name: 'managementAreaId',
			label: 'Área Gestora do Contrato',
			required: true,
			placeholder: 'Selecione a área gestora',
			options: solicitantAreas?.filter((area) => area.managementTeamId),
			isLoading: isLoadingAreas,
			allowClear: true,
			rules: { required: 'Selecione uma área gestora' },
			attacheds: [
				[{ key: 'categoryId', values: [selectedWorkflowCategory?.id ?? null] }],
			],
		},
		{
			type: 'select',
			name: 'templateId',
			label: 'Modelo',
			required: true,
			options: models,
			isLoading: isLoadingModels,
			rules: { required: 'Selecione um modelo' },
			onChange: handleTemplateChange,
			attacheds: [[{ key: '_requireTemplate', values: [true] }]],
		},
		{
			type: 'alert',
			name: '_moduleTooltipAlert',
			alertType: 'info',
			alertMessage: 'Informações do modelo',
			alertDescription: (vals) => vals._moduleTooltip || '',
			attacheds: [
				[
					{ key: '_requireTemplate', values: [true] },
					{ key: '_hasModuleTooltip', values: [true] },
				],
			],
		},
		{
			type: 'select',
			name: 'priority',
			label: 'Prioridade',
			required: true,
			options: priorities,
			rules: { required: 'Selecione a prioridade' },
		},
		{
			type: 'select',
			name: 'technicalAreaTeamId',
			label: 'Área técnica',
			required: true,
			options: technicalAreas,
			isLoading: isLoadingTechnicalAreas,
			rules: { required: 'Selecione a área técnica' },
			attacheds: [
				[{ key: 'categoryId', values: [selectedWorkflowCategory?.id ?? null] }],
			],
		},
		...(isAllCareUser
			? [
					{
						type: 'select',
						name: 'analysisObjectId',
						label: 'Objeto das Análises',
						required: true,
						options: analysisObjects,
						isLoading: isLoadingAnalysisObjects,
						rules: { required: 'Selecione o objeto de análise' },
					},
					{
						type: 'textarea',
						name: 'resume',
						label: 'Resumo da demanda',
						required: true,
						rules: { required: 'Selecione o objeto de análise' },
					},
				]
			: []),
		{
			type: 'uploader',
			name: 'fileList',
			label: 'Anexos',
			required: isAllCareUser,
			multiple: true,
			max: 5,
			allowedTypes: Object.keys(mimeTypes),
			allowedExtensions: Object.values(mimeTypes),
		},
	]

	const vendorReviewFields = VendorReviewFormQuestions.map((question) => ({
		type: vendorReviewFieldTypeMap[question.type],
		name: vendorReviewFieldName(question.key),
		label: question.label,
		required: question.required || Boolean(question.conditional),
		options: question.options?.map((option) => ({
			id: option,
			name: option,
		})),
		...(question.conditional
			? {
					attacheds: [
						[
							{
								key: vendorReviewFieldName(question.conditional.dependsOn),
								values: [question.conditional.showWhenIncludes],
							},
						],
					],
				}
			: {}),
	}))

	const hasPreview = false

	const { questions, lastPage } = useSelector(
		({ question }) => ({
			questions: question.data,
			lastPage: hasPreview ? question.lastPage + 1 : question.lastPage,
		}),
		shallowEqual
	)

	const visible = useSelector(({ question }) => question.visible)
	const currentPage = useSelector(({ question }) => question.currentPage)
	const isLastPage = currentPage === lastPage

	const handleNextStep = async () => {
		const isValid = await trigger()
		if (!isValid) return

		if (requireTemplateFields && !isUploadTemplate) {
			setCurrentStep(1)
			return
		}

		if (requiresVendorReviewForm) {
			setCurrentStep(2)
			return
		}

		handleSubmit(onSubmit)()
	}

	const handleBackNavigation = () => {
		if (currentStep === 2) {
			setCurrentStep(requireTemplateFields && !isUploadTemplate ? 1 : 0)
			return
		}

		if (currentStep === 1) {
			if (currentPage > 0) {
				dispatch(previousPage())
			} else {
				setCurrentStep(0)
			}
		}
	}

	const onNext = async () => {
		try {
			await templateVariablesForm.validateFields()

			dispatch(
				appendAnswer({
					data: templateVariablesForm.getFieldsValue(),
					pageFieldsData: questions[currentPage],
					visible: visible[currentPage],
				})
			)

			if (!isLastPage) {
				dispatch(nextPage({ hasPreview }))
			} else if (requiresVendorReviewForm) {
				setCurrentStep(2)
			} else {
				handleSubmit((values) =>
					onSubmit({ ...values, variables: JSON.stringify(answer.data) })
				)()
			}
		} catch (e) {}
	}

	const handleFinalSubmit = handleSubmit((values) => {
		const hasTemplateVariablesStep = requireTemplateFields && !isUploadTemplate
		onSubmit(
			hasTemplateVariablesStep
				? { ...values, variables: JSON.stringify(answer.data) }
				: values
		)
	})

	const isStep0Valid = useMemo(() => {
		const requiredFields = step0Fields.filter((field) => {
			if (field.type === 'alert') return false
			if (!field.required) return false
			if (field.attacheds) {
				const attachment = field.attacheds[0]
				if (attachment) {
					return attachment.some((cond) => {
						const fieldValue = watchValues[cond.key]
						return cond.values.includes(fieldValue)
					})
				}
			}
			return true
		})

		return requiredFields.every((field) => {
			const value = watchValues[field.name]
			if (field.type === 'uploader') {
				return Array.isArray(value) && value.length > 0
			}
			return value !== undefined && value !== null && value !== ''
		})
	}, [watchValues])

	const isStep2Valid = useMemo(() => {
		const requiredFields = vendorReviewFields.filter((field) => {
			if (!field.required) return false
			if (field.attacheds) {
				const attachment = field.attacheds[0]
				if (attachment) {
					return attachment.some((cond) => {
						const fieldValue = watchValues[cond.key]
						if (Array.isArray(fieldValue)) {
							return cond.values.some((value) => fieldValue.includes(value))
						}
						return cond.values.includes(fieldValue)
					})
				}
			}
			return true
		})

		return requiredFields.every((field) => {
			const value = watchValues[field.name]
			if (Array.isArray(value)) return value.length > 0
			return value !== undefined && value !== null && value !== ''
		})
	}, [watchValues, vendorReviewFields])

	return (
		<div className={styles.layout}>
			<PageHeader
				title="Nova Demanda"
				onBack={handleGoBack}
				className={styles.header}
			/>

			<div className={styles.content}>
				<StyledForm layout="vertical">
					{currentStep === 0 && (
						<div className={styles.formSection}>
							<FormSection
								title="Informações da demanda"
								description="Preencha cuidadosamente as informações da demanda
								para que o responsável jurídico tenha um entendimento claro sobre
								a solicitação."
							/>

							{step0Fields.map((fieldConfig) => (
								<FieldForm
									key={fieldConfig.name}
									config={fieldConfig}
									control={control}
									setValue={setValue}
									watchValues={enrichedWatchValues}
								/>
							))}
						</div>
					)}

					{currentStep === 1 && watchValues.templateId && !isUploadTemplate && (
						<div className={styles.formSection}>
							<FormSection
								title="Informações do documento"
								description="Preencha com as informações necessárias para elaboração do contrato."
							/>
							<FormFactory form={templateVariablesForm} />
						</div>
					)}

					{currentStep === 2 && (
						<div className={styles.formSection}>
							<FormSection
								title="Due diligence de contratação de fornecedor"
								description="Responda as perguntas abaixo para a análise jurídica da contratação."
							/>

							{vendorReviewFields.map((fieldConfig) => (
								<FieldForm
									key={fieldConfig.name}
									config={fieldConfig}
									control={control}
									setValue={setValue}
									watchValues={watchValues}
								/>
							))}
						</div>
					)}

					<div className={styles.footer}>
						{currentStep === 0 && (
							<Button
								type="primary"
								onClick={handleNextStep}
								loading={isLoadingDemand}
								disabled={!isStep0Valid}>
								{(requireTemplateFields && !isUploadTemplate) ||
								requiresVendorReviewForm
									? 'Próxima etapa'
									: 'Cadastrar demanda'}
							</Button>
						)}
						{currentStep === 1 && (
							<div style={{ display: 'flex', gap: 8 }}>
								<Button onClick={handleBackNavigation}>Voltar</Button>
								<Button
									type="primary"
									onClick={onNext}
									loading={isLoadingDemand}>
									{isLastPage ? 'Cadastrar demanda' : 'Próxima etapa'}
								</Button>
							</div>
						)}
						{currentStep === 2 && (
							<div style={{ display: 'flex', gap: 8 }}>
								<Button onClick={handleBackNavigation}>Voltar</Button>
								<Button
									type="primary"
									onClick={handleFinalSubmit}
									loading={isLoadingDemand}
									disabled={!isStep2Valid}>
									Cadastrar demanda
								</Button>
							</div>
						)}
					</div>
				</StyledForm>
			</div>
		</div>
	)
}

export default CreateDemand
